import { useState } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import {
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
  BrokenImageRounded,
  CloseRounded,
} from "@mui/icons-material";
import { ServiceDocument } from "../../model/service_details_model";
import { greyEnabled, purpleLight } from "../../../../theme/colors";

const overlayButtonSx = {
  bgcolor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
};

function GalleryThumbnail({
  doc,
  onClick,
}: {
  doc: ServiceDocument;
  onClick: () => void;
}) {
  const [errored, setErrored] = useState(false);

  if (errored || !doc?.url) {
    return (
      <Box
        mr={2}
        mt={2}
        height={150}
        width={260}
        flexShrink={0}
        borderRadius={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={0.5}
        sx={{ bgcolor: purpleLight }}
      >
        <BrokenImageRounded sx={{ color: greyEnabled, fontSize: 32 }} />
        <Typography variant="caption" color={greyEnabled}>
          Image unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box mr={2} mt={2} flexShrink={0} borderRadius={3} sx={{ cursor: "pointer" }} onClick={onClick}>
      <img
        alt=""
        src={doc.url}
        height={150}
        width={260}
        style={{ borderRadius: "8px", objectFit: "cover", display: "block" }}
        onError={() => setErrored(true)}
      />
    </Box>
  );
}

export default function ServiceImageGallery({
  title,
  images,
}: {
  title: string;
  images: ServiceDocument[] | undefined | null;
}) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [viewerErrored, setViewerErrored] = useState(false);

  if (!images || images.length === 0) return null;

  const closeViewer = () => setViewerIndex(null);
  const openViewer = (index: number) => {
    setViewerErrored(false);
    setViewerIndex(index);
  };
  const showPrev = () => {
    setViewerErrored(false);
    setViewerIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  };
  const showNext = () => {
    setViewerErrored(false);
    setViewerIndex((i) => (i === null ? null : (i + 1) % images.length));
  };

  const activeImage = viewerIndex !== null ? images[viewerIndex] : null;

  return (
    <Box mt={3}>
      <Typography variant="body1" fontWeight="600">
        {title}
      </Typography>
      <Box display="flex" flexDirection="row" overflow="auto">
        {images.map((doc, index) => (
          <GalleryThumbnail key={doc?.url ?? index} doc={doc} onClick={() => openViewer(index)} />
        ))}
      </Box>

      <Dialog
        open={viewerIndex !== null}
        onClose={closeViewer}
        maxWidth={false}
        PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none", m: 0 } }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: { xs: "80vw", sm: 400 },
            minHeight: 200,
          }}
        >
          <IconButton onClick={closeViewer} sx={{ ...overlayButtonSx, position: "absolute", top: 8, right: 8 }}>
            <CloseRounded />
          </IconButton>
          {images.length > 1 && (
            <IconButton onClick={showPrev} sx={{ ...overlayButtonSx, position: "absolute", left: 8 }}>
              <ArrowBackIosNewRounded fontSize="small" />
            </IconButton>
          )}
          {viewerErrored || !activeImage?.url ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              py={8}
              px={10}
            >
              <BrokenImageRounded sx={{ color: "#fff", fontSize: 48 }} />
              <Typography color="#fff">This image couldn't be loaded.</Typography>
            </Box>
          ) : (
            <img
              alt=""
              src={activeImage.url}
              style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, objectFit: "contain", display: "block" }}
              onError={() => setViewerErrored(true)}
            />
          )}
          {images.length > 1 && (
            <IconButton onClick={showNext} sx={{ ...overlayButtonSx, position: "absolute", right: 8 }}>
              <ArrowForwardIosRounded fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
